const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;
// Re-encode if the file is an image AND bigger than this. Tiny icons or
// already-optimized screenshots aren't worth round-tripping through canvas.
const COMPRESS_MIN_BYTES = 300 * 1024; // 300 KB

const COMPRESSIBLE_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
]);

/**
 * If `file` is a large bitmap image, re-encodes it as JPEG at ≤1600px on the
 * long edge and ~85% quality. Returns the original file unchanged when it's
 * not an image, already small, or if anything in the pipeline fails — the
 * goal is best-effort size reduction, never blocking the user.
 */
export async function compressImageIfNeeded(file: File): Promise<File> {
  if (!COMPRESSIBLE_MIME.has(file.type)) return file;
  if (file.size <= COMPRESS_MIN_BYTES) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = scaleToFit(
      bitmap.width,
      bitmap.height,
      MAX_DIMENSION,
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // White background — JPEG has no alpha, so transparent PNGs would render
    // black without this.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;

    const renamed = file.name.replace(/\.[a-z0-9]+$/i, '.jpg');
    return new File([blob], renamed, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to <img> fallback (Safari quirks with some formats)
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function scaleToFit(srcW: number, srcH: number, max: number) {
  if (srcW <= max && srcH <= max) return { width: srcW, height: srcH };
  const ratio = srcW > srcH ? max / srcW : max / srcH;
  return {
    width: Math.round(srcW * ratio),
    height: Math.round(srcH * ratio),
  };
}
